import React, { useState } from "react";
import { Modal, Button, Select, Spin, Alert, Table, Space, Typography, Tag } from "antd";
import { PrinterOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { getStageInstructions, createInstructionLog } from "../../api/batchPlanningApi";
import { getActiveTemplate, seedDefaultTemplates } from "../../api/printTemplateApi";
import moment from "moment";

const { Text } = Typography;

const PRODUCTION_STAGES = [
  { code: "corrugation",     label: "Corrugation",        hindi: "नालीदार" },
  { code: "sheet_cutting",   label: "Sheet Cutting",       hindi: "शीट कटिंग" },
  { code: "pasting",         label: "Pasting",             hindi: "चिपकाई" },
  { code: "casting",         label: "Casting",             hindi: "ढलाई" },
  { code: "pre_print",       label: "Pre-Print",           hindi: "पूर्व छपाई" },
  { code: "coating",         label: "Coating",             hindi: "कोटिंग" },
  { code: "die_cutting",     label: "Die Cutting",         hindi: "डाई कटिंग" },
  { code: "joining",         label: "Joining / Stitching", hindi: "जोड़ाई / सिलाई" },
  { code: "after_box_print", label: "After-Box Print",     hindi: "बॉक्स छपाई" },
  { code: "qc",              label: "Quality Check",       hindi: "गुणवत्ता जांच" },
];

// ── Hindi translations: field keys → display labels ───────────────────────────
const HI = {
  // doc
  "Combined Work Instructions": "संयुक्त कार्य निर्देश",
  "Generated":          "तैयार किया",
  "batch":              "बैच",
  "batches":            "बैच",
  "Production Work Order": "उत्पादन कार्य आदेश",
  "Current stage":      "वर्तमान चरण",
  "jobs":               "काम",
  "job":                "काम",
  "pcs":                "पीस",
  // stage detail headers
  "Job Number":         "काम नं.",
  "Job Qty":            "काम मात्रा",
  "Item Name":          "वस्तु नाम",
  "Item":               "वस्तु",
  "Rs":                 "RS",
  "Cs":                 "CS",
  "Ply":                "प्लाई",
  "Box Qty":            "बक्सा",
  "Box Quantity":       "बक्सा",
  "Sheet Qty":          "शीट",
  "Sheet Quantity":     "शीट",
  "M":                  "M",
  "N":                  "N",
  "Waste":              "बर्बादी",
  "Liner Kg":           "लाइनर (किग्रा)",
  "Liner Material":     "लाइनर सामग्री",
  "Flute Kg":           "नालीदार (किग्रा)",
  "Flute Material":     "नालीदार सामग्री",
  "Flute Factor":       "नालीदार गुणक",
  "Total Paper Kg":     "कुल कागज (किग्रा)",
  "Gum Kg":             "गोंद (किग्रा)",
  "Two Ply Kg":         "दो-प्लाई (किग्रा)",
  "Two Ply Sheets":     "दो-प्लाई शीट",
  "Two Ply Material":   "दो-प्लाई सामग्री",
  "Top Paper Kg":       "टॉप कागज (किग्रा)",
  "Top Paper Sheets":   "टॉप शीट",
  "Top Paper Material": "टॉप कागज सामग्री",
  "Boxes Per Sheet":    "बक्से/शीट",
  "Sheets Per Box":     "शीट/बक्सा",
  "Priority":           "प्राथमिकता",
  "Delivery":           "डिलीवरी",
  "Print Method":       "छपाई विधि",
  "Printing Sides":     "छपाई साइड",
  "Printing Colors":    "छपाई रंग",
  "Joining Method":     "जोड़ाई विधि",
  "Box Weight":         "बक्सा वजन",
  "Net Gsm":            "नेट GSM",
  "L":                  "लं.",
  "B":                  "चौ.",
  "H":                  "ऊं.",
  // totals
  "sheet_qty":          "शीट संख्या",
  "box_qty":            "बक्सा संख्या",
  "liner_kg":           "लाइनर (किग्रा)",
  "flute_kg":           "नालीदार (किग्रा)",
  "total_paper_kg":     "कुल कागज (किग्रा)",
  "gum_kg":             "गोंद (किग्रा)",
  "two_ply_kg":         "दो-प्लाई (किग्रा)",
  "two_ply_sheets":     "दो-प्लाई शीट",
  "top_paper_kg":       "टॉप कागज (किग्रा)",
  "top_paper_sheets":   "टॉप शीट",
  // signoff
  "Operator":           "ऑपरेटर",
  "Start":              "शुरू",
  "End":                "समाप्त",
  "Output Qty":         "उत्पादन",
  "Wastage":            "बर्बादी",
  // materials
  "Totals":             "कुल",
  "Materials":          "सामग्री",
  "Stage Details":      "चरण विवरण",
  // stage names
  "Corrugation":        "नालीदार",
  "Sheet Cutting":      "शीट कटिंग",
  "Pasting":            "चिपकाई",
  "Casting":            "ढलाई",
  "Pre-Print":          "पूर्व छपाई",
  "Coating":            "कोटिंग",
  "Die Cutting":        "डाई कटिंग",
  "Joining / Stitching":"जोड़ाई / सिलाई",
  "After-Box Print":    "बॉक्स छपाई",
  "Quality Check":      "गुणवत्ता जांच",
};

function t(word, lang) {
  if (lang !== "hi") return word;
  return HI[word] || word;
}

// Convert a snake_case/camelCase key to Title Case for lookup
function keyToTitle(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Flatten a job object so nested fields are top-level for template lookup ───
// e.g. job.order.customer_po_number → flat.customer_po_number
function flattenJob(job = {}) {
  if (!job) return {};
  const flat = { ...job };
  const order = job.order || {};
  flat.order_number       = order.order_number       ?? flat.order_number;
  flat.customer_po_number = order.customer_po_number ?? null;
  flat.expected_delivery  = order.expected_delivery  ?? flat.expected_delivery;
  flat.priority           = order.priority           ?? flat.priority;
  flat.customer_name      = order.customer?.name     ?? null;
  flat.shipping_address   = order.shipping_address   ?? null;
  flat.order_notes        = order.notes              ?? null;
  return flat;
}

// ── Apply a template's column config to a unified_table ─────────────────────
// jobsLookup: { [job_number]: jobObject } for enriching rows with Order/Job fields
// Returns: { headers: [{key, label, width, align}], rows: string[][] }
function applyTemplate(unified, templateColumns, lang, jobsLookup = {}) {
  if (!unified || unified.length === 0) return { headers: [], rows: [] };

  const rawKeys = Object.keys(unified[0]);

  let colDefs;
  if (templateColumns && templateColumns.length > 0) {
    // Use template — show visible columns in order; do NOT filter by rawKeys
    // because some columns (e.g. customer_po_number) come from enriched job data
    colDefs = templateColumns
      .filter((c) => c.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  } else {
    // Fallback: all keys present in the instruction data
    colDefs = rawKeys.map((k, i) => ({
      key: k, order: i + 1, visible: true, labels: { en: keyToTitle(k) },
    }));
  }

  const headers = colDefs.map((c) => ({
    key: c.key,
    label: (lang === "hi" && c.labels?.hi) ? c.labels.hi : (c.labels?.en || keyToTitle(c.key)),
    width: c.width || "",
    align: c.align || "left",
    bold: c.bold === true,
  }));

  const rows = unified.map((row) => {
    // Enrich the row with flattened job/order fields so template columns can
    // reference any Job or Order field, not just what's in the unified_table.
    const flatJob = flattenJob(jobsLookup[row.job_number]);
    const enriched = { ...flatJob, ...row }; // instruction row values take precedence

    return colDefs.map((c) => {
      const val = enriched[c.key];
      if (val == null || val === "") return "–";
      // Format dates nicely
      if (c.key === "expected_delivery" && val && !isNaN(Date.parse(val))) {
        return new Date(val).toLocaleDateString("en-IN");
      }
      return String(val);
    });
  });

  return { headers, rows };
}

// ── Build complete print HTML ─────────────────────────────────────────────────
function buildPrintHTML(printData, stageName, lang, templateColumns = null) {
  const hi = lang === "hi";

  const css = `
    @page { size: A4 landscape; margin: 14mm 16mm; }
    * { box-sizing: border-box; }
    body { font-family: ${hi ? "'Noto Sans Devanagari', " : ""}Arial, Helvetica, sans-serif; font-size: 13pt; color: #000; margin: 0; padding-top: 60px; }
    #print-bar { position: fixed; top: 0; left: 0; right: 0; z-index: 999; background: #1a6fcc; color: #fff; display: flex; align-items: center; justify-content: space-between; padding: 10px 20px; font-family: Arial, sans-serif; font-size: 14px; }
    #print-bar button { background: #fff; color: #1a6fcc; border: none; border-radius: 4px; padding: 7px 20px; font-size: 14px; font-weight: 700; cursor: pointer; }
    #print-bar button:hover { background: #e6f0ff; }
    @media print { #print-bar { display: none !important; } body { padding-top: 0; } }
    h1 { font-size: 18pt; margin: 0 0 4px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 12px; page-break-inside: auto; }
    th { background: #e0e0e0; font-weight: 700; padding: 8px 11px; border: 1px solid #999; text-align: left; font-size: 12pt; }
    td { padding: 7px 11px; border: 1px solid #bbb; font-size: 12pt; vertical-align: middle; }
    tr:nth-child(even) td { background: #f7f7f7; }
    .doc-header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #1a6fcc; padding-bottom: 10px; margin-bottom: 22px; }
    .doc-header-right { text-align: right; font-size: 11pt; color: #444; }
    .batch-header { background: #dbeafe; border-left: 6px solid #1a6fcc; padding: 10px 16px; margin: 24px 0 12px; display: flex; gap: 18px; align-items: center; flex-wrap: wrap; }
    .badge { background: #c5d9f8; padding: 2px 10px; border-radius: 3px; font-size: 12pt; font-weight: 600; }
    .batch-number { font-size: 15pt; font-weight: 700; }
    .section-title { font-size: 13pt; font-weight: 700; margin: 14px 0 7px; color: #222; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
    .totals-box { background: #f5f5f5; border: 1px solid #ccc; padding: 9px 14px; margin-bottom: 12px; font-size: 13pt; }
    .materials-box { background: #fffbe6; border: 1px solid #ffe58f; padding: 9px 14px; margin-bottom: 12px; font-size: 13pt; }
    .signoff { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0; border-top: 2px solid #999; margin-top: 14px; }
    .signoff-cell { padding: 12px 14px; border: 1px solid #bbb; font-size: 13pt; }
    .signoff-label { font-weight: 700; font-size: 11pt; color: #444; display: block; margin-bottom: 6px; }
    .batch-divider { border: none; border-top: 2px dashed #ccc; margin: 24px 0; }
    @media print { .batch-divider { page-break-after: avoid; } }
  `;

  // Font link for Hindi
  const fontLink = hi
    ? `<link rel="preconnect" href="https://fonts.googleapis.com">
       <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet">`
    : "";

  function batchHTML(batchData, idx) {
    const { batch_number, roll_size, cutting_size, flute_type, total_quantity, jobs = [], instruction, current_stage } = batchData;
    const content = instruction?.content || {};
    const unified = content.unified_table || [];
    const totals = content.totals || {};
    const materials = instruction?.materials || [];

    // Build a lookup map: job_number → flattened job object for row enrichment
    const jobsLookup = {};
    (jobs || []).forEach((job) => { if (job.job_number) jobsLookup[job.job_number] = job; });

    // Stage detail table — filtered/ordered by active template
    let stageTableHTML = "";
    if (unified.length > 0) {
      const { headers: colHeaders, rows: colRows } = applyTemplate(unified, templateColumns, lang, jobsLookup);
      if (colHeaders.length > 0) {
        const colgroup = colHeaders.map((h) => `<col style="width:${h.width || "auto"}">`).join("");
        const headerHtml = colHeaders.map((h) =>
          `<th style="text-align:${h.align}${h.bold ? ";font-weight:900" : ""}">${h.label}</th>`
        ).join("");
        const rowHtml = colRows.map((cells) =>
          `<tr>${cells.map((v, ci) => {
            const h = colHeaders[ci];
            return `<td style="text-align:${h.align}${h.bold ? ";font-weight:700" : ""}">${v}</td>`;
          }).join("")}</tr>`
        ).join("");
        const sectionTitle = `${t(stageName, lang)} — ${t("Stage Details", lang)}`;
        stageTableHTML = `
          <div class="section-title">${sectionTitle}</div>
          <table><colgroup>${colgroup}</colgroup><thead><tr>${headerHtml}</tr></thead><tbody>${rowHtml}</tbody></table>
        `;
      }
    }

    // Totals
    const totalsHTML = Object.keys(totals).length > 0
      ? `<div class="totals-box"><b>${t("Totals", lang)} — </b>${
          Object.entries(totals).map(([k, v]) =>
            `<span style="margin-right:28px"><b>${t(k, lang) || keyToTitle(k)}:</b> ${v}</span>`
          ).join("")
        }</div>`
      : "";

    // Materials
    const materialsHTML = materials.length > 0
      ? `<div class="materials-box"><b>${t("Materials", lang)} — </b>${
          materials.map((m) =>
            `<span style="margin-right:28px"><b>${m.name}:</b> ${m.quantity} ${m.unit}</span>`
          ).join("")
        }</div>`
      : "";

    const divider = idx > 0 ? `<hr class="batch-divider">` : "";
    const stageDisplay = (current_stage || "").replace(/_/g, " ");

    return `
      ${divider}
      <div class="batch-header">
        <span class="batch-number">${batch_number}</span>
        <span class="badge">Roll Size: ${roll_size}"</span>
        <span style="font-size:12pt;color:#444">
          ${jobs.length} ${t(jobs.length === 1 ? "job" : "jobs", lang)} &nbsp;·&nbsp;
          <b>${total_quantity?.toLocaleString() ?? 0}</b> ${t("pcs", lang)}
        </span>
        <span style="font-size:11pt;color:#666;margin-left:auto">
          ${t("Current stage", lang)}: ${stageDisplay}
        </span>
      </div>

      ${stageTableHTML || `<p style="color:#888;font-style:italic;font-size:12pt">No ${stageName} instruction data found for this batch.</p>`}
      ${totalsHTML}
      ${materialsHTML}

      <div class="signoff">
        <div class="signoff-cell"><span class="signoff-label">${t("Operator", lang)}</span>________________________</div>
        <div class="signoff-cell"><span class="signoff-label">${t("Start", lang)}</span>________________</div>
        <div class="signoff-cell"><span class="signoff-label">${t("End", lang)}</span>________________</div>
        <div class="signoff-cell"><span class="signoff-label">${t("Output Qty", lang)}</span>________________</div>
        <div class="signoff-cell"><span class="signoff-label">${t("Wastage", lang)}</span>________________</div>
      </div>
    `;
  }

  const now = new Date().toLocaleString();
  const titleLabel = hi
    ? `${t(stageName, lang)} — ${t("Combined Work Instructions", lang)}`
    : `${stageName} — Combined Work Instructions`;
  const batchesHTML = (printData.batches || []).map((b, i) => batchHTML(b, i)).join("");
  const count = printData.batches?.length ?? 0;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${titleLabel}</title>
  ${fontLink}
  <style>${css}</style>
</head>
<body>
  <div id="print-bar">
    <span>📄 ${titleLabel} &nbsp;·&nbsp; ${count} ${t(count === 1 ? "batch" : "batches", lang)}</span>
    <button onclick="window.print()">🖨️ Print / Save PDF</button>
  </div>
  <div class="doc-header">
    <div>
      <h1>${titleLabel}</h1>
      <div style="font-size:11pt;color:#555">
        ${t("Generated", lang)}: ${now} &nbsp;·&nbsp; ${count} ${t(count === 1 ? "batch" : "batches", lang)}
      </div>
    </div>
    <div class="doc-header-right">
      <div style="font-size:15pt;font-weight:700">Dabba ERP</div>
      <div>${t("Production Work Order", lang)}</div>
    </div>
  </div>
  ${batchesHTML}
</body>
</html>`;
}

// ── Main modal ────────────────────────────────────────────────────────────────
const MachineInstructions = ({ open, onClose }) => {
  const [selectedStage, setSelectedStage] = useState(null);
  const [lang, setLang] = useState("en");
  const [allBatches, setAllBatches] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleLoadBatches = async () => {
    if (!selectedStage) return;
    setLoading(true);
    setAllBatches([]);
    setSelectedIds([]);
    setPrintData(null);
    try {
      const result = await getStageInstructions(selectedStage);
      setAllBatches(result.batches || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedIds.length) return;
    setGenerating(true);
    try {
      const result = await getStageInstructions(selectedStage, selectedIds);
      setPrintData(result);
      const selectedBatches = allBatches.filter((b) => selectedIds.includes(b.batch_id));
      createInstructionLog({
        stage_code: selectedStage,
        stage_name: PRODUCTION_STAGES.find((s) => s.code === selectedStage)?.label,
        batch_ids: selectedIds,
        batch_numbers: selectedBatches.map((b) => b.batch_number),
        total_jobs: selectedBatches.reduce((s, b) => s + (b.job_count || 0), 0),
        total_quantity: selectedBatches.reduce((s, b) => s + (b.total_quantity || 0), 0),
      }).catch(() => {});
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = async () => {
    const stageInfo = PRODUCTION_STAGES.find((s) => s.code === selectedStage);

    // Fetch the active template for this stage (seed defaults if none exist)
    let templateColumns = null;
    try {
      const template = await getActiveTemplate("machine_instructions", selectedStage);
      if (!template) {
        // Seed and retry once
        await seedDefaultTemplates();
        const seeded = await getActiveTemplate("machine_instructions", selectedStage);
        templateColumns = Array.isArray(seeded?.columns) ? seeded.columns : null;
      } else {
        templateColumns = Array.isArray(template.columns) ? template.columns : null;
      }
    } catch (e) {
      // Non-fatal — falls back to all columns
    }

    const html = buildPrintHTML(printData, stageInfo?.label || selectedStage, lang, templateColumns);
    const win = window.open("", "_blank", "width=1200,height=900");
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  const handleClose = () => {
    setSelectedStage(null);
    setAllBatches([]);
    setSelectedIds([]);
    setPrintData(null);
    onClose();
  };

  const stageInfo = PRODUCTION_STAGES.find((s) => s.code === selectedStage);

  const pickerColumns = [{
    title: "Batch",
    render: (_, r) => {
      const deliveries = (r.jobs || []).map((j) => j.expected_delivery).filter(Boolean).map((d) => new Date(d).getTime());
      const earliest = deliveries.length ? new Date(Math.min(...deliveries)) : null;
      const daysLeft = earliest ? moment(earliest).diff(moment(), "days") : null;
      return (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Text strong>{r.batch_number}</Text>
          <Tag style={{ fontSize: 11 }}>RS {r.roll_size}"</Tag>
          {r.cutting_size && <Tag style={{ fontSize: 11 }}>CS {r.cutting_size}"</Tag>}
          {r.flute_type && <Tag color="geekblue" style={{ fontSize: 11 }}>{r.flute_type}</Tag>}
          <Tag color={r.current_stage === "pending_inventory" ? "gold" : "blue"} style={{ fontSize: 10 }}>
            {r.current_stage?.replace(/_/g, " ")}
          </Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.job_count} jobs · {r.total_quantity?.toLocaleString()} pcs</Text>
          {earliest && (
            <Tag color={daysLeft < 0 ? "red" : daysLeft <= 3 ? "orange" : "green"} style={{ fontSize: 10 }}>
              {moment(earliest).format("DD MMM")} ({daysLeft < 0 ? "overdue" : `${daysLeft}d`})
            </Tag>
          )}
        </div>
      );
    },
  }];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={<Space><PrinterOutlined /><span>Machine / Stage Instructions</span></Space>}
      width={900}
      style={{ top: 20 }}
      footer={
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <Button onClick={handleClose}>Close</Button>
          <Space>
            {!printData ? (
              <>
                <Button onClick={handleLoadBatches} loading={loading} disabled={!selectedStage}>Load Batches</Button>
                <Button type="primary" icon={<ArrowRightOutlined />} onClick={handleGenerate} loading={generating} disabled={!selectedIds.length}>
                  Generate ({selectedIds.length} selected)
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setPrintData(null)}>← Back</Button>
                <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>Open Print Preview</Button>
              </>
            )}
          </Space>
        </Space>
      }
    >
      {/* Controls */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <Text strong>Stage:</Text>
        <Select
          style={{ width: 210 }}
          placeholder="Select stage"
          value={selectedStage}
          onChange={(v) => { setSelectedStage(v); setAllBatches([]); setSelectedIds([]); setPrintData(null); }}
          options={PRODUCTION_STAGES.map((s) => ({ value: s.code, label: s.label }))}
        />
        <Text strong style={{ marginLeft: 8 }}>Language:</Text>
        <Select
          style={{ width: 140 }}
          value={lang}
          onChange={setLang}
          options={[
            { value: "en", label: "English" },
            { value: "hi", label: "हिंदी (Hindi)" },
          ]}
        />
      </div>

      {/* Step 1: Batch picker */}
      {!printData && (
        <>
          {loading && <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>}
          {!loading && allBatches.length === 0 && (
            <Alert
              message={selectedStage ? "No active batches found. Click Load Batches." : "Select a stage and click Load Batches."}
              type="info" showIcon
            />
          )}
          {!loading && allBatches.length > 0 && (
            <>
              <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {allBatches.length} active batch{allBatches.length !== 1 ? "es" : ""} — tick to include
                </Text>
                <Space>
                  <Button size="small" onClick={() => setSelectedIds(allBatches.map((b) => b.batch_id))}>Select All</Button>
                  <Button size="small" onClick={() => setSelectedIds([])}>Clear</Button>
                </Space>
              </div>
              <Table
                size="small"
                dataSource={allBatches}
                rowKey="batch_id"
                pagination={false}
                showHeader={false}
                columns={pickerColumns}
                rowSelection={{ selectedRowKeys: selectedIds, onChange: setSelectedIds }}
                scroll={{ y: 360 }}
              />
            </>
          )}
        </>
      )}

      {/* Step 2: Ready summary */}
      {printData && (
        <div>
          <Alert
            type="success"
            showIcon
            message={`Instructions ready for ${printData.batches?.length} batch${printData.batches?.length !== 1 ? "es" : ""} — ${stageInfo?.label}`}
            description={`Language: ${lang === "hi" ? "हिंदी" : "English"}. Click 'Open Print Preview' to open the full-page print window.`}
            style={{ marginBottom: 16 }}
          />
          {(printData.batches || []).map((b) => (
            <div key={b.batch_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 10px", background: "#f5f5f5", borderRadius: 4, marginBottom: 4 }}>
              <Text strong>{b.batch_number}</Text>
              <Tag style={{ fontSize: 11 }}>RS {b.roll_size}"</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>{b.job_count} jobs · {b.total_quantity?.toLocaleString()} pcs</Text>
              {b.instruction
                ? <Tag color="green" style={{ marginLeft: "auto", fontSize: 11 }}>Ready</Tag>
                : <Tag color="orange" style={{ marginLeft: "auto", fontSize: 11 }}>No saved instruction</Tag>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default MachineInstructions;
